import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { fabric } from 'fabric';
import { ToastVariants } from '@dotcom/crown-ui';
import { getPhotoTextZoneSettings } from '../../../components/card-editor/utils';
import { setIsToasterOpen, useAppContext } from '../../../context/app-context';
import { useInitializationDataContext } from '../../../context/data-context';
import { CardText, FabricTextBox } from '../../../global-types';
import { getFontName, CanvasDataTypes, PHOTO_TEXT_ZONE_DEFAULT_FONT_ID } from '../../../utils';
import { addPhotoTextZoneButton, PhotoTextZone } from '../utils/add-photo-text-zone-button';
import { tamFitsInZone } from '../utils/tam-fits-in-zone';

// Explicit types for hook functions
type OnUserZoneError = (error: Error) => void;

type CheckTextZoneBoundsProps = { textbox: fabric.Textbox; photoTextZone: fabric.Group };

type LoadPhotoTextZoneProps = { zone: fabric.Group };

type AddPhotoTextZoneProps = {
  canvas: React.RefObject<fabric.Canvas>;
  text: CardText;
  zoneIndex: number;
  zoneLabel: string;
  isShowingAltLabel?: boolean;
};

export const usePhotoTextZone = () => {
  const { appDispatch } = useAppContext();
  const { t } = useTranslation();
  const { initializedDataState } = useInitializationDataContext();
  const { isUK } = initializedDataState;

  // Handles user zone errors by showing a toaster and throwing the error
  const onUserZoneError: OnUserZoneError = (error) => {
    setIsToasterOpen(appDispatch, {
      title: t('generalError.title'),
      children: t('generalError.message'),
      variant: ToastVariants.Error,
    });

    throw error;
  };

  // Checks if the textbox is within the photo text zone and shows a warning if not
  const checkTextZoneBounds = useCallback(
    ({ textbox, photoTextZone }: CheckTextZoneBoundsProps): void => {
      const isTextInsideZone = tamFitsInZone({ textbox, photoTextZone });
      if (isTextInsideZone) {
        photoTextZone.data.warned = false;
        return;
      }

      if (!photoTextZone.data.warned) {
        photoTextZone.data.warned = true;
        setIsToasterOpen(appDispatch, {
          title: t('typeMessage.warningTitle'),
          children: t('typeMessage.warningMessage'),
          variant: ToastVariants.Warning,
        });
      }
    },
    [appDispatch, t],
  );

  // Loads a photo text zone and attaches a change listener to its textbox
  const loadPhotoTextZone = useCallback(
    ({ zone }: LoadPhotoTextZoneProps): void => {
      // PRCM-10897: zones deserialized from a saved project don't come with
      // our hasBorders/stroke settings applied. Reapply them here so a
      // reloaded card doesn't flash the fabric selection border on first tap.
      zone.set({ hasBorders: false, stroke: 'transparent' });
      const textbox = zone._objects.find((obj) => obj.type === 'textbox') as fabric.Textbox | undefined;
      if (textbox) {
        // Same defensive re-application on the inner textbox: fabric may
        // paint its own selection border during enterEditing, causing a
        // brief flash.
        textbox.set({ hasBorders: false });
        textbox.on('changed', () => {
          checkTextZoneBounds({ textbox, photoTextZone: zone });
        });
      }
    },
    [checkTextZoneBounds],
  );

  // Adds a new photo text zone to the canvas with appropriate settings and controls
  const addPhotoTextZone = useCallback(
    async ({ canvas, text, zoneIndex, zoneLabel }: AddPhotoTextZoneProps): Promise<void> => {
      const currentCanvas = canvas.current;
      if (!currentCanvas) {
        onUserZoneError(new Error('Canvas is not defined'));
        return;
      }
      // Get base zone settings
      const zoneSettings = getPhotoTextZoneSettings(text, zoneIndex, isUK);
      // Add Clip path so that text respects bounding box
      const clipPath = new fabric.Rect({
        ...zoneSettings,
        absolutePositioned: true,
      });
      // Add textbox to be populated with TAM.
      const originalText = new fabric.Textbox('', {
        textAlign: 'center',
        fontWeight: 'normal',
        fontSize: 32,
        top: Math.ceil(zoneSettings.top - zoneSettings.height / 2),
        left: Math.ceil(zoneSettings.left - zoneSettings.width / 2),
        fontFamily: getFontName(PHOTO_TEXT_ZONE_DEFAULT_FONT_ID),
        width: zoneSettings.width,
        hasBorders: false,
        lockMovementX: true,
        lockMovementY: true,
        lockRotation: true,
        controls: {},
        data: {
          linkedZoneName: zoneSettings.name,
          ID: text.ID,
        },
        CanResizeTextArea: zoneSettings.CanResizeTextArea,
        CanEditFontSize: zoneSettings.CanEditFontSize,
        CanEditFontColor: zoneSettings.CanEditFontColor,
        FontAutoSize: zoneSettings.FontAutoSize,
        name: `${CanvasDataTypes.PhotoZoneTextbox}-${zoneIndex}`,
        clipPath,
      } as CardText & FabricTextBox);

      // Get dotted photoTextZone border
      const photoTextZoneBorder = new fabric.Rect(zoneSettings);

      // PRCM-10897: only the inner rect above renders the visible dashed
      // border. The outer group inherits the same settings by design (for
      // positioning, sizing, clip path, etc.), but must NOT re-stroke or
      // fabric will paint a second, offset dashed line right next to the
      // inner one. Keep strokeWidth intact so getScaledWidth/Height still
      // matches the original bounds (tam-fits-in-zone depends on it); only
      // hide the color to make the second stroke invisible.
      // Also suppress the group's fabric selection border (drawn from the
      // prototype defaults in set-custom-controls when the group is the
      // active object) so we don't paint a second purple dashed line around
      // the inner rect's stroke when the zone is selected.
      const photoTextZoneGroupSettings = {
        ...zoneSettings,
        stroke: 'transparent',
        hasBorders: false,
      };

      // Add all objects to photoTextZone group
      const photoTextZone = new fabric.Group([originalText, photoTextZoneBorder], photoTextZoneGroupSettings);

      originalText.on('changed', function (this: fabric.Textbox) {
        checkTextZoneBounds({ textbox: this, photoTextZone });
      });

      currentCanvas.add(photoTextZone);

      // Use non-dynamic button behavior
      addPhotoTextZoneButton({
        zone: photoTextZone as PhotoTextZone,
        currentCanvas,
        label: zoneLabel,
      });
    },
    [getPhotoTextZoneSettings, getFontName, CanvasDataTypes, addPhotoTextZoneButton, t, checkTextZoneBounds, isUK],
  );

  return { addPhotoTextZone, loadPhotoTextZone, checkTextZoneBounds };
};
